import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { ControlContainer, FormControl, ReactiveFormsModule } from '@angular/forms';
import { IonTextarea } from '@ionic/angular/standalone';
import {
  effectiveOptionalRequired,
  explicitPlaceholder,
  withOptionalAriaLabel,
  withOptionalLabelSuffix,
} from '../form-optional-display';

@Component({
  selector: 'crowdctrl-form-textarea-field',
  standalone: true,
  imports: [IonTextarea, ReactiveFormsModule],
  templateUrl: './form-textarea-field.component.html',
  styleUrls: [
    '../form-outline-field-tokens.css',
    '../form-control-hint.css',
    './form-textarea-field.component.css',
  ],
  encapsulation: ViewEncapsulation.None,
})
export class FormTextareaFieldComponent implements OnChanges {
  private controlDisabledByComponent = false;
  @Input() invalid = false;
  @Input() hint = '';
  @Input() controlName?: string;
  @Input() label?: string;
  @Input() placeholder?: string;
  /**
   * When true: appends ` (optional)` to the label (unless already present) and clears the
   * required affordance. Use a **value-hint** {@link placeholder} like required fields — never
   * the word “Optional” as the placeholder.
   */
  @Input() optional = false;
  @Input() required = false;
  @Input() disabled = false;
  @Input() rows?: number;
  @Input() maxlength?: number;
  @Input() minlength?: number;
  @Input() inputmode?: string;
  @Input() autocapitalize: string = 'sentences';
  @Input() spellcheck?: boolean;
  @Input() ariaLabel?: string;
  /**
   * Grow with the parent flex column (e.g. order notes pane). Flex sizing applies to this host and
   * the wrap; **not** `min-height` on `ion-textarea` — avoid device-specific textarea floors.
   */
  @Input() fillAvailableHeight = false;

  @Output() ionInput = new EventEmitter<Event>();
  @Output() ionChange = new EventEmitter<Event>();
  @Output() ionBlur = new EventEmitter<Event>();
  @Output() ionFocus = new EventEmitter<Event>();

  protected formControl: FormControl | null = null;

  @HostBinding('class.crowdctrl-form-textarea-field--fill-available')
  get hostFillAvailableClass(): boolean {
    return this.fillAvailableHeight;
  }

  get effectiveLabel(): string | undefined {
    return withOptionalLabelSuffix(this.optional, this.label);
  }

  get effectivePlaceholder(): string | undefined {
    return explicitPlaceholder(this.placeholder);
  }

  get effectiveRequired(): boolean {
    return effectiveOptionalRequired(this.required, this.optional);
  }

  get effectiveAriaLabel(): string | undefined {
    return withOptionalAriaLabel(this.optional, this.ariaLabel, this.label);
  }

  constructor(private readonly controlContainer: ControlContainer) {}

  ngOnChanges(): void {
    this.formControl = this.resolveFormControl();
    if (!this.formControl) {
      return;
    }

    if (this.disabled) {
      if (this.formControl.enabled) {
        this.formControl.disable({ emitEvent: false });
        this.controlDisabledByComponent = true;
      }
      return;
    }

    if (this.controlDisabledByComponent && this.formControl.disabled) {
      this.formControl.enable({ emitEvent: false });
      this.controlDisabledByComponent = false;
    }
  }

  private resolveFormControl(): FormControl | null {
    if (!this.controlName) {
      return null;
    }

    const control = this.controlContainer.control?.get(this.controlName);
    return control instanceof FormControl ? control : null;
  }
}
